import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { clp } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";

const EMPTY = { name: "", ot_id: null, location: "", responsible_id: null, status: "activo" };

const WarehouseDialog = ({ edit, setEdit, onSaved }) => {
  const [lk, setLk] = useState({ ots: [], users: [] });
  useEffect(() => { if (edit) Promise.all([api.get("/abastecimiento/ots"), api.get("/abastecimiento/users-lookup")]).then(([o, u]) => setLk({ ots: o.data, users: u.data })); }, [edit]);
  const save = async () => {
    const { id, type, ...body } = edit;
    try { id ? await api.patch(`/abastecimiento/warehouses/${id}`, body) : await api.post("/abastecimiento/warehouses", body); toast.success("Bodega guardada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent data-testid="warehouse-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Editar bodega" : "Nueva sub-bodega"}</DialogTitle></DialogHeader>
        {edit && <div className="space-y-3">
          <Field label="Nombre *"><Input data-testid="wh-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          {edit.type !== "central" && <Field label="OT (bodega de faena)"><Pick testid="wh-ot-select" value={edit.ot_id} onChange={(v) => setEdit({ ...edit, ot_id: v === "none" ? null : v })} options={[{ id: "none", name: "Sin OT" }, ...lk.ots]} placeholder="Sin OT" /></Field>}
          <Field label="Ubicación"><Input data-testid="wh-location-input" value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} /></Field>
          <Field label="Responsable"><Pick testid="wh-responsible-select" value={edit.responsible_id} onChange={(v) => setEdit({ ...edit, responsible_id: v })} options={lk.users} placeholder="Seleccione" /></Field>
          {edit.id && edit.type !== "central" && <Field label="Estado"><Pick testid="wh-status-select" value={edit.status} onChange={(v) => setEdit({ ...edit, status: v })} options={[{ id: "activo", name: "Activa" }, { id: "inactivo", name: "Inactiva" }]} /></Field>}
        </div>}
        <DialogFooter><Button data-testid="wh-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Warehouses() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/abastecimiento/warehouses", { params: { include_inactive: true } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const canEdit = (w) => can(w.type === "central" ? "abastecimiento.bodega_central.gestionar" : "abastecimiento.subbodegas.gestionar");
  return (
    <div data-testid="warehouses-page">
      <PageHeader eyebrow="Abastecimiento" title="Bodegas" subtitle="Bodega Central y sub-bodegas de faena. Cada bodeguero ve solo las bodegas autorizadas."
        actions={can("abastecimiento.subbodegas.gestionar") && <Button data-testid="create-warehouse-btn" className="rounded-full" onClick={() => setEdit({ ...EMPTY })}><Plus size={16} className="mr-1" />Nueva sub-bodega</Button>} />
      {list.length === 0 ? <div className="syc-card"><EmptyState title="Sin bodegas autorizadas" /></div> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{list.map((w) => (
          <div key={w.id} data-testid={`wh-card-${w.id}`} className={`syc-card p-5 cursor-pointer transition-transform hover:-translate-y-0.5 ${w.status === "activo" ? "" : "opacity-50"}`} onClick={() => navigate(`/abastecimiento/bodegas/${w.id}`)}>
            <div className="flex items-start gap-3">
              <span className={`h-11 w-11 rounded-xl grid place-items-center ${w.type === "central" ? "bg-[#17210f] text-[#5CD500]" : "bg-[#e9f7e1] text-[#258F00]"}`}><Warehouse size={20} /></span>
              <div className="flex-1 min-w-0"><div className="font-mono text-xs text-[#5b6656]">{w.code}{w.ot_code ? ` · ${w.ot_code}` : ""}</div><div className="font-bold truncate">{w.name}</div></div>
              {canEdit(w) && <button data-testid={`wh-edit-${w.id}`} className="text-xs font-semibold text-[#258F00]" onClick={(e) => { e.stopPropagation(); setEdit({ id: w.id, type: w.type, name: w.name, ot_id: w.ot_id, location: w.location, responsible_id: w.responsible_id, status: w.status }); }}>Editar</button>}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span><b>{w.items}</b> productos</span>{w.value !== undefined && <span className="text-[#5b6656]">· {clp(w.value)}</span>}
              {w.alerts > 0 && <StatusBadge status="pendiente" label={`${w.alerts} alertas de mínimo`} />}
              {w.status !== "activo" && <StatusBadge status="inactivo" label="Inactiva" />}
            </div>
          </div>))}</div>)}
      <WarehouseDialog edit={edit} setEdit={setEdit} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
